<template>
    <div class="flex flex-col items-center gap-6 md:gap-8 w-full max-w-4xl">
        <div class="text-center leading-tight">
            <div class="max-w-2xl display-medium text-balance mb-4">
                <span class="gradient-text">Choose how to explore</span>
            </div>
            <p class="text-valence-grey-750 body md:display-serif max-w-2xl mx-auto text-pretty">Each path shows a different side of what Nadia can do.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 w-full">
            <button
                v-for="option in options"
                :key="option.id"
                class="group border-2 rounded-xl p-5 md:p-6 bg-white transition-all duration-200 text-left"
                :class="[
                    option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md',
                    selectedOption === option.id ? 'border-indigo-600 bg-indigo-50/30 shadow-lg ring-2 ring-indigo-600/20' : 'border-gray-200',
                ]"
                :disabled="option.disabled"
                @click="!option.disabled && selectOption(option.id)">
                <div class="flex flex-col h-full gap-3">
                    <div class="flex items-center gap-3">
                        <div class="flex h-10 w-10 items-center justify-center rounded-lg" :class="selectedOption === option.id ? 'bg-indigo-100' : 'bg-gray-100'">
                            <i :class="`bi ${option.icon} text-xl`" :style="{ color: selectedOption === option.id ? '#4f46e5' : '#6b7280' }" aria-hidden="true"></i>
                        </div>
                        <span
                            v-if="option.badge"
                            class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                            :class="{
                                'bg-indigo-100 text-indigo-700': option.badge === 'Recommended',
                                'bg-gray-200 text-gray-500': option.badge === 'Coming soon',
                                'bg-gray-100 text-gray-600': option.badge !== 'Recommended' && option.badge !== 'Coming soon',
                            }">
                            {{ option.badge }}
                        </span>
                    </div>
                    <h3 class="title-body text-gray-900">{{ option.title }}</h3>
                    <p class="body-small text-gray-600 flex-1 text-pretty">{{ option.description }}</p>
                    <p class="text-xs text-gray-400 mt-2">{{ option.detail }}</p>
                </div>
            </button>
        </div>

        <div class="flex items-center gap-4 mt-2">
            <BaseButton theme="tertiary" @click="emit('back')"> <i class="bi bi-arrow-left mr-1" aria-hidden="true"></i> Back </BaseButton>
            <BaseButton theme="primary" class="w-60 leading-6 body-small" :disabled="!selectedOption" @click="handleContinue">
                {{ selectedOption ? "Get started" : "Select an option" }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { ref } from "vue";

export type ExperienceOption = "preloaded" | "fresh" | "admin";

const emit = defineEmits<{
    select: [option: ExperienceOption];
    back: [];
}>();

const selectedOption = ref<ExperienceOption | null>(null);

const options = [
    {
        id: "preloaded" as const,
        icon: "bi-lightning-charge-fill",
        title: "Experience the Day 60 version",
        description: "See what Nadia looks like with weeks of accumulated context. Pre-loaded with a realistic leadership profile.",
        detail: "Best for seeing the ceiling",
        badge: "Recommended",
        disabled: false,
    },
    {
        id: "fresh" as const,
        icon: "bi-person-plus",
        title: "Kick the tires yourself",
        description: "Try Nadia from scratch — this is the most basic version and not representative of what employees would experience with full context.",
        detail: "Best for a personal trial",
        badge: null,
        disabled: false,
    },
    {
        id: "admin" as const,
        icon: "bi-building",
        title: "See the admin view",
        description: "Explore the organizational perspective — analytics, deployment tools, and team-level insights.",
        detail: "Best for HR and L&D buyers",
        badge: "Coming soon",
        disabled: true,
    },
];

function selectOption(option: ExperienceOption) {
    selectedOption.value = option;
}

function handleContinue() {
    if (selectedOption.value) {
        emit("select", selectedOption.value);
    }
}
</script>
